/**
 * Wireline & Slickline helpers (WL-1): who may press what, how a refusal
 * reads, the well identifier by country, and the line-state labels.
 *
 * Copied in shape from lib/well-servicing.ts, never imported from it: each
 * vertical owns its own helpers (cross-vertical isolation).
 *
 * NOTHING HERE COMPUTES A LINE FIGURE. The length on the drum, the due dates
 * and the status are folded by the API (fieldiq-api lib/wl-line.ts); this file
 * only turns the API's values into words. Absent renders as `—`.
 */
import type { WlDueStatus, WlMeta } from '../types/wireline-slickline';
import { fmtQty } from './display-labels';

// ─── Who may press what (mirrors fieldiq-api lib/wl-roles.ts) ────────────────
//
// For hiding a button, not for enforcement. A 403 that still arrives is shown
// as a refusal, never as success.

export const WL_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'field_worker', 'logistics'] as const;
export const WL_ADMIN_ROLES = ['admin', 'office_staff'] as const;
export const WL_WELL_WRITE_ROLES = ['admin', 'office_staff', 'supervisor'] as const;
export const WL_CREW_WRITE_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;
export const WL_GEAR_WRITE_ROLES = ['admin', 'office_staff', 'supervisor'] as const;

const inList = (list: readonly string[]) => (role: string | null | undefined) => !!role && list.includes(role);
/** Settings, customer settings, unit create / edit / delete, every delete, and voiding a line event. */
export const canAdminWl = inList(WL_ADMIN_ROLES);
/** Well create / edit. */
export const canWriteWlWells = inList(WL_WELL_WRITE_ROLES);
/** Crew create / edit / delete and crew members. */
export const canWriteWlCrews = inList(WL_CREW_WRITE_ROLES);
/** Line create / edit, line events, tool-string create / edit / items. */
export const canWriteWlGear = inList(WL_GEAR_WRITE_ROLES);

// ─── Refusals ────────────────────────────────────────────────────────────────

export interface WlApiError {
  status: number | null;
  code: string | null;
  message: string | null;
  extra: Record<string, unknown>;
}

export function wlApiError(err: unknown): WlApiError {
  const r = (err as { response?: { status?: unknown; data?: { error?: unknown; code?: unknown } } })?.response;
  const d = r?.data;
  return {
    status: typeof r?.status === 'number' ? r.status : null,
    code: typeof d?.code === 'string' ? d.code : null,
    message: typeof d?.error === 'string' && d.error ? d.error : null,
    extra: d && typeof d === 'object' ? (d as Record<string, unknown>) : {},
  };
}

export const WL_PERMISSION_REFUSAL = 'You do not have permission to do this. Ask an admin or office staff.';

/**
 * A write's error as one plain sentence. A 403 is a permission refusal, a bare
 * 404 reads as gone; every coded WL-1 refusal (duplicate_well,
 * duplicate_unit_number, exceeds_length, not_spooled, already_voided, …)
 * carries a sentence from the API, so it is shown as sent; else `fallback`.
 */
export function wlRefusal(err: unknown, fallback: string): string {
  const e = wlApiError(err);
  if (e.status === 403) return WL_PERMISSION_REFUSAL;
  if (e.status === 404 && !e.code) return 'That record no longer exists. It may have been deleted.';
  return e.message ?? fallback;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/** The 409 duplicate_well: the API's sentence and the existing well to link to. */
export function duplicateWlWell(err: unknown): { message: string; existingWellId: string | null } | null {
  const e = wlApiError(err);
  if (e.status !== 409 || e.code !== 'duplicate_well') return null;
  const what = e.extra.field === 'api_number' ? 'API number' : 'UWI';
  return { message: e.message ?? `Another well already has this ${what}.`, existingWellId: str(e.extra.existingWellId) };
}

/** A 409 naming an existing record (`existing<Kind>Id`), for a link beside the sentence. */
export function existingIdOf(err: unknown, key: 'existingUnitId' | 'existingLineId'): string | null {
  const e = wlApiError(err);
  return e.status === 409 ? str(e.extra[key]) : null;
}

// ─── Identifier by country ───────────────────────────────────────────────────

export const wlIsUs = (meta: Pick<WlMeta, 'country'> | null | undefined) => meta?.country === 'US';
export const wlWellIdLabel = (meta: Pick<WlMeta, 'country'> | null | undefined) => (wlIsUs(meta) ? 'API number' : 'UWI');
export const wlWellIdOf = (
  well: { uwi: string | null; apiNumber: string | null },
  meta: Pick<WlMeta, 'country'> | null | undefined,
): string | null => (wlIsUs(meta) ? well.apiNumber : well.uwi);

// ─── Display ─────────────────────────────────────────────────────────────────

/** A number as given, grouped; absent → `—`. A real 0 is `0`. */
export const fmtWlNumber = (n: number | null | undefined): string => fmtQty(n, 6);

/** `1,200 m`; absent → `—` with no unit after it. */
export function wlWithUnit(n: number | null | undefined, unit: string | null | undefined): string {
  const v = fmtWlNumber(n);
  if (v === '—') return v;
  return unit ? `${v} ${unit}` : v;
}

/** A length the API sends in metres (a tool string's total): `4,500 m`, absent `—`. Never converted here. */
export const wlMetres = (n: number | null | undefined): string => wlWithUnit(n, 'm');

/**
 * A line-state length in the tenant's unit, as the API sent it (`lengthOnDrum`
 * with `lengthUnit`): `14,722.1 ft`, absent `—`. With no unit from the API the
 * figure is `—` too: a bare number would read as either unit. Never converted.
 */
export const wlLineLength = (n: number | null | undefined, unit: string | null | undefined): string =>
  unit ? wlWithUnit(n, unit) : '—';

/** The label the API's catalog gives a value; the raw value if the catalog lacks it; `—` when absent. */
export function wlCatalogLabel(list: readonly { value: string; label: string }[] | undefined, value: string | null | undefined): string {
  if (!value) return '—';
  return list?.find((e) => e.value === value)?.label ?? value;
}

export const wlBlankToNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());
export const wlToInput = (v: number | string | null | undefined): string => (v === null || v === undefined ? '' : String(v));

/** 'HH:MM', 24-hour (mirrors the API's day_sheet_start_time check). */
export const WL_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export interface WlSkin {
  label: string;
  fill: string;
  rail: string;
}

/** A well has no status: its rail claims no colour. Sour is a flag, drawn as a badge. */
export const WL_RAIL = 'border-l-transparent';
export const WL_SOUR_SKIN: WlSkin = { label: 'Sour', fill: 'bg-fi-signal-red', rail: 'border-l-transparent' };
export const WL_RETIRED_SKIN: WlSkin = { label: 'Retired', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' };
export const WL_VOIDED_SKIN: WlSkin = { label: 'Voided', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' };

/**
 * The API's due status as words and a colour. The status is the API's: this
 * map only names it. An unknown status shows as itself.
 */
export const WL_DUE_SKIN: Record<WlDueStatus, WlSkin> = {
  not_tracked: { label: 'Not tracked', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  never_tested: { label: 'Never tested', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  ok: { label: 'In date', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  overdue: { label: 'Overdue', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  failed: { label: 'Failed', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

export function wlDueSkin(status: string | null | undefined): WlSkin {
  if (!status) return { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
  return (WL_DUE_SKIN as Record<string, WlSkin>)[status] ?? { label: status, fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
}

/** A status the overview lists as needing attention. A filter on the API's value, not a figure. */
export const WL_ATTENTION_STATUSES: readonly string[] = ['overdue', 'failed', 'never_tested'];

/**
 * What a line event takes, by type (mirrors fieldiq-api parseEvent). For which
 * inputs to SHOW; the API is what refuses a missing one, and its sentence is
 * shown as sent.
 */
export const WL_EVENT_FIELDS: Record<string, { length: 'required' | 'optional' | null; drum: 'required' | 'optional' | null; load: boolean; result: 'required' | 'optional' | null }> = {
  spooled: { length: 'required', drum: 'optional', load: false, result: null },
  cut: { length: 'required', drum: null, load: false, result: null },
  splice: { length: 'optional', drum: null, load: false, result: null },
  rehead: { length: 'optional', drum: null, load: false, result: null },
  pull_test: { length: null, drum: null, load: true, result: 'required' },
  inspection: { length: null, drum: null, load: false, result: 'optional' },
  moved_to_drum: { length: null, drum: 'required', load: false, result: null },
  retired: { length: null, drum: null, load: false, result: null },
};

export const isWlElectricLine = (lineType: string | null | undefined) => lineType === 'eline_mono' || lineType === 'eline_multi';
